/**
 * Inline content (text runs, links, page links, line breaks) → Markdown or
 * HTML (git sync Stage 2, S2.1). Two modes: `md` is the readable form;
 * `html` is the exact fallback a caller picks when the Markdown form does
 * not read back identically (an odd bold/italic boundary, a formatted space).
 *
 * Lexical text format bits: 1 bold · 2 italic · 4 strikethrough · 8 underline
 * · 16 code · 32 subscript · 64 superscript · 128 highlight.
 */
export const FMT = Object.freeze({ bold: 1, italic: 2, strike: 4, underline: 8, code: 16, sub: 32, sup: 64, mark: 128 });

/** Formats Markdown can say itself; the rest become tags even in `md` mode. */
const MD_DELIMS = [
  [FMT.bold, '**'],
  [FMT.italic, '*'],
  [FMT.strike, '~~'],
];
const TAGS = [
  [FMT.bold, 'b'],
  [FMT.italic, 'i'],
  [FMT.strike, 's'],
  [FMT.underline, 'u'],
  [FMT.sub, 'sub'],
  [FMT.sup, 'sup'],
  [FMT.mark, 'mark'],
  [FMT.code, 'code'],
];

const HTML_ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' };
export const escHtml = (s) => String(s).replace(/[&<>"]/g, (c) => HTML_ESC[c]);

/**
 * Text as Markdown source: every character Markdown could read as syntax is
 * escaped, and spaces Markdown would drop (at the start/end of a line) become
 * an entity.
 */
export function escMd(text) {
  let s = String(text)
    .replace(/\\/g, '\\\\')
    .replace(/([`*_[\]<>~|!#])/g, '\\$1')
    .replace(/&(?=[a-zA-Z#][a-zA-Z0-9]*;)/g, '&amp;');
  return s;
}

/** Markdown-safe text for HTML mode inside a Markdown paragraph: tags stay tags, nothing else is syntax. */
function escMdHtml(text) {
  return escHtml(text).replace(/([\\`*_[\]~|!#])/g, (c) => `&#${c.charCodeAt(0)};`);
}

function hrefOk(href) {
  return String(href || '').replace(/[()<>\s]/g, (c) => encodeURIComponent(c));
}

/** A link title, when the node has one. */
const titlePart = (title) => (title ? ` "${String(title).replace(/"/g, '\\"')}"` : '');

/**
 * @param {Array} children   Lexical inline JSON (text, linebreak, tab, link, page-link)
 * @param {object} ctx       { pageLinkHref(node) }
 * @param {'md'|'html'} mode
 */
export function renderInline(children, ctx, mode = 'md') {
  let out = '';
  for (const node of children || []) out += renderInlineNode(node, ctx, mode);
  return mode === 'md' ? protectEdgeSpaces(out) : out;
}

/** Leading / trailing spaces of a Markdown line are not content to a parser — keep them as entities. */
function protectEdgeSpaces(s) {
  return s.replace(/^ +/, (m) => '&#32;'.repeat(m.length)).replace(/ +$/, (m) => '&#32;'.repeat(m.length));
}

function renderInlineNode(node, ctx, mode) {
  switch (node.type) {
    case 'text':
    case 'code-highlight':
      return renderText(node, mode);
    case 'tab':
      return '\t';
    case 'linebreak':
      // A hard break: backslash + newline in Markdown, <br> in HTML.
      return mode === 'md' ? '\\\n' : '<br>';
    case 'link': {
      const inner = renderInline(node.children, ctx, mode === 'md' ? 'md' : 'html');
      if (mode === 'html') {
        const attrs = [`href="${escHtml(node.url)}"`];
        if (node.title) attrs.push(`title="${escHtml(node.title)}"`);
        if (node.target) attrs.push(`target="${escHtml(node.target)}"`);
        if (node.rel) attrs.push(`rel="${escHtml(node.rel)}"`);
        return `<a ${attrs.join(' ')}>${inner}</a>`;
      }
      if (node.target || node.rel || /[\s()<>]/.test(node.url || '')) {
        return `<a href="${escHtml(node.url)}"${node.title ? ` title="${escHtml(node.title)}"` : ''}${node.target ? ` target="${escHtml(node.target)}"` : ''}${node.rel ? ` rel="${escHtml(node.rel)}"` : ''}>${renderInline(node.children, ctx, 'html')}</a>`;
      }
      return `[${inner}](${node.url}${titlePart(node.title)})`;
    }
    case 'page-link': {
      const href = ctx.pageLinkHref(node);
      return mode === 'md'
        ? `[${escMd(node.title || '')}](${hrefOk(href)})`
        : `<a href="${escHtml(href)}">${escMdHtml(node.title || '')}</a>`;
    }
    default:
      throw new Error(`docMarkdown: no inline form for "${node.type}"`);
  }
}

function renderText(node, mode) {
  const format = node.format || 0;
  const text = node.text || '';
  const styled = node.style ? (inner) => `<span style="${escHtml(node.style)}">${inner}</span>` : (inner) => inner;

  if (mode === 'html') {
    let inner = escMdHtml(text);
    for (const [bit, tag] of [...TAGS].reverse()) if (format & bit) inner = `<${tag}>${inner}</${tag}>`;
    return styled(inner);
  }

  // Markdown mode. Inline code alone is a code span; with any other format it is a tag.
  if (format === FMT.code && !node.style) return codeSpan(text);
  let inner = format & FMT.code ? `<code>${escMdHtml(text)}</code>` : escMd(text);
  for (const [bit, tag] of TAGS) {
    if (bit === FMT.code || bit === FMT.bold || bit === FMT.italic || bit === FMT.strike) continue;
    if (format & bit) inner = `<${tag}>${inner}</${tag}>`;
  }
  for (const [bit, delim] of [...MD_DELIMS].reverse()) if (format & bit) inner = `${delim}${inner}${delim}`;
  return styled(inner);
}

/** A code span whose fence is longer than any backtick run inside it. */
function codeSpan(text) {
  const runs = String(text).match(/`+/g) || [];
  const fence = '`'.repeat(Math.max(0, ...runs.map((r) => r.length)) + 1);
  const pad = /^`|`$|^ .* $/.test(text) ? ' ' : '';
  return `${fence}${pad}${text}${pad}${fence}`;
}
